// SyncBanner — 3-State-Header (Patch 5, Sprint 2 Tag 10).
//
// State-Logik kommt vom Backend (/api/v1/sync/status), Frontend
// rendert nur. Trust-Killer-Fix: niemals 'fresh' bei konten_count=0.
//
// Sprint 7: no_accounts-State ist dismissible via sessionStorage,
// damit Mobile-Screen-1 nicht durchgehend von der Banking-Warnung
// belegt wird, solange EBICS noch nicht aktiviert ist.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { SyncStatus } from "../lib/api";
import { IconAlertCircle, IconCheckCircle, IconClock } from "./EmptyTile";

const SS_DISMISS_KEY = "sync-banner-no-accounts-dismissed";

interface Props {
  data?: SyncStatus;
  loading?: boolean;
}

const STATE_STYLE = {
  no_accounts: {
    bg: "bg-amber-500/10 border-amber-500/40",
    text: "text-amber-200",
    icon: IconAlertCircle,
  },
  fresh: {
    bg: "bg-emerald-500/10 border-emerald-500/40",
    text: "text-emerald-200",
    icon: IconCheckCircle,
  },
  stale: {
    bg: "bg-amber-500/15 border-amber-500/50",
    text: "text-amber-100",
    icon: IconClock,
  },
} as const;

export default function SyncBanner({ data, loading }: Props) {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Nur no_accounts ist persistent dismissible. fresh/stale sind transient.
    if (data?.state === "no_accounts") {
      try {
        setDismissed(sessionStorage.getItem(SS_DISMISS_KEY) === "1");
      } catch {
        // SSR / Privacy-Mode → kein Dismiss-State, Banner immer sichtbar.
      }
    } else {
      setDismissed(false);
    }
  }, [data?.state]);

  if (loading) {
    return (
      <div
        data-testid="sync-banner"
        className="rounded-xl border border-slate-700/60 bg-slate-900 px-4 py-2 text-xs text-slate-500"
      >
        Lade Sync-Status …
      </div>
    );
  }
  if (!data) {
    return (
      <div
        data-testid="sync-banner"
        className="rounded-xl border border-slate-700/60 bg-slate-900 px-4 py-2 text-xs text-slate-500"
      >
        Sync-Status nicht verfügbar
      </div>
    );
  }

  // Dismissed no_accounts → Banner versteckt fuer Session.
  if (data.state === "no_accounts" && dismissed) {
    return null;
  }

  const style = STATE_STYLE[data.state];
  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SS_DISMISS_KEY, "1");
    } catch {
      // Privacy-Mode — Dismiss bleibt fluechtig.
    }
    setDismissed(true);
  };

  return (
    <div
      data-testid="sync-banner"
      data-sync-state={data.state}
      className={`rounded-xl border ${style.bg} px-4 py-2 flex items-center justify-between gap-2 text-xs ${style.text}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="[&_svg]:h-4 [&_svg]:w-4 shrink-0">{style.icon}</span>
        <span className="font-medium truncate">{data.message}</span>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {data.cta_label && data.cta_href && (
          <Link
            to={data.cta_href}
            className="inline-flex items-center min-h-11 px-2 font-medium underline underline-offset-2 hover:opacity-80"
          >
            → {data.cta_label}
          </Link>
        )}
        {data.state === "no_accounts" && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Banking-Warnung schliessen (Session)"
            className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-slate-800/60 text-slate-400"
          >
            <span aria-hidden>×</span>
          </button>
        )}
      </div>
    </div>
  );
}
